import { useEffect, useRef } from "react";
import { useApp } from "@/context/AppContext";

export const ytId = (url) => {
  if (!url) return null;
  const m = String(url).match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : (/^[A-Za-z0-9_-]{11}$/.test(url) ? url : null);
};

let ytApiPromise = null;
function loadYTApi() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { prev && prev(); resolve(window.YT); };
    if (!document.getElementById("yt-iframe-api")) {
      const tag = document.createElement("script");
      tag.id = "yt-iframe-api"; tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
    }
  });
  return ytApiPromise;
}

/**
 * Lecteur YouTube qui coupe la musique d'ambiance pendant la lecture (duckForVideo)
 * et la relance à la pause/fin. Remplace les <iframe> YouTube bruts.
 */
export default function YouTubeEmbed({ videoId, url, title = "video", style, frameStyle }) {
  const { duckForVideo } = useApp();
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const playingRef = useRef(false);
  const id = videoId || ytId(url);

  useEffect(() => {
    let destroyed = false;
    if (!id) return undefined;
    loadYTApi().then((YT) => {
      if (destroyed || !hostRef.current) return;
      playerRef.current = new YT.Player(hostRef.current, {
        videoId: id,
        playerVars: { rel: 0, modestbranding: 1, playsinline: 1 },
        events: {
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.PLAYING) {
              if (!playingRef.current) { playingRef.current = true; duckForVideo(true); }
            } else if (e.data === YT.PlayerState.PAUSED || e.data === YT.PlayerState.ENDED) {
              if (playingRef.current) { playingRef.current = false; duckForVideo(false); }
            }
          },
        },
      });
    });
    return () => {
      destroyed = true;
      if (playingRef.current) { playingRef.current = false; duckForVideo(false); }
      try { playerRef.current?.destroy(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!id) return null;
  return (
    <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", borderRadius: 14, overflow: "hidden", background: "#000", ...style }}>
      <div ref={hostRef} title={title} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none", ...frameStyle }} />
    </div>
  );
}
